(ns app.front.view.group.admin.templates.stamps.core
  (:require [app.domain.group.model :as group-model]
            [app.domain.stamp.model :as stamp-model]
            [app.front.ui :as ui]
            [app.front.ui.table :as ui-table]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.templates.stamps.builder :as builder]
            [app.front.view.group.admin.templates.stamps.canvas :as canvas]
            [app.front.view.group.admin.templates.stamps.subs :as subs]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.permission.resolver :as permission-resolver]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(def stamps-tab-param "stamps")

(defn table-location [group-id]
  {:route  :view/group.admin.templates
   :params {:group-id group-id}
   :query  {:tab stamps-tab-param}})

(defn table-href [group-id]
  (href/href :view/group.admin.templates {:group-id group-id} {:tab stamps-tab-param}))

(defn stamps-breadcrumb [{:keys [tr match]}]
  (let [{:keys [route params query]} (table-location (-> match :parameters :path :group-id))]
    [route params (tr [:stamp/stamps]) query]))

(defn builder-breadcrumb [{:keys [tr match]}]
  [(-> match :data :name)
   (-> match :parameters :path)
   (tr [(if (-> match :parameters :path :stamp-id) :stamp/edit :stamp/add)])])

(rf/reg-sub ::view-data
  :<- [:route/match]
  (fn [match _]
    {:back-href (table-href (-> match :parameters :path :group-id))}))

(defn open-new-builder! [group-id]
  (>evt [:route/push {:route  :view/group.admin.stamp.new
                      :params {:group-id group-id}}]))

(defn open-edit-builder! [group-id stamp-id]
  (>evt [:route/push {:route  :view/group.admin.stamp.edit
                      :params {:group-id group-id :stamp-id stamp-id}}]))

(defn confirmation-modal [tr]
  (let [{:keys [open? message on-confirm]} (<sub [::subs/confirmation])]
    [ui/confirm-modal
     {:id           "stamp-confirmation"
      :tr           tr
      :open?        (boolean open?)
      :modal-title  message
      :on-close     #(>evt [::subs/close-confirmation])
      :on-yes-click #(>evt on-confirm)}]))

(defn collection-screen [tr group-id]
  (let [{:keys [loading? error busy? context]} (<sub [::subs/collection])]
    [:section.bg-white.space-y-4
     [confirmation-modal tr]
     [:h3.text-lg.font-bold
      (tr [:stamp/stamps])]
     (cond
       loading?
       [:p {:role "status"}
        (tr [:loading])]

       error
       [:div
        [:p {:role "alert"}
         (tr [error])]
        [ui/button
         {:kind     :tertiary
          :on-click #(>evt [::subs/load-collection group-id])}
         (tr [:stamp/retry])]]

       :else
       [:div.space-y-4
        [ui/button
         {:class-name "mb-4"
          :on-click   #(open-new-builder! group-id)
          :disabled   (boolean busy?)}
         (tr [:stamp/add])]
        [ui-table/modern-responsive-table
         {:tr                tr
          :items             (:stamps context)
          :id-func           :id
          :cols              [[(tr [:stamp/name]) :name]
                              [(tr [:stamp/creator]) #(if (str/blank? (:creator-name %)) "—" (:creator-name %))]]
          :empty-placeholder (tr [:stamp/empty])
          :expanded-func
          (fn [{:keys [assets layout] :as stamp}]
            [:div.p-4.space-y-4
             [:div.max-w-lg
              [canvas/preview
               {:tr       tr
                :snapshot (merge context {:image-urls (stamp-model/image-urls group-id assets)
                                          :layout     layout
                                          :date       (.format date-util/calendar-date-format (js/Date.))})}]]
             [:div.flex.gap-3
              [ui/button
               {:kind     :tertiary
                :disabled (boolean busy?)
                :on-click #(open-edit-builder! group-id (:id stamp))}
               (tr [:edit])]
              [ui/button
               {:kind     :danger
                :size     :small
                :disabled (boolean busy?)
                :on-click #(>evt [::subs/request-delete stamp (tr [:stamp/delete-confirm])])}
               (tr [:delete])]]])}]])]))

(def collection-controller
  {:parameters {:path [:group-id] :query [:tab]}
   :start      (fn [{{:keys [group-id]} :path {:keys [tab]} :query}]
                 (when (= stamps-tab-param tab)
                   (>evt [::subs/load-collection group-id])))
   :stop       (fn [{{:keys [tab]} :query}]
                 (when (= stamps-tab-param tab)
                   (>evt [::subs/clear-collection])))})

(defn builder-view [tr]
  (let [{:keys [loading? missing? invalid? error draft group-id stamp-id] :as data} (<sub [::subs/editor])
        return-route                                                                (table-location group-id)]
    [:section.bg-white.space-y-4
     [confirmation-modal tr]
     (cond
       loading?
       [:p {:role "status"}
        (tr [:loading])]

       missing?
       [:p {:role "alert"}
        (tr [:stamp/not-found])]

       invalid?
       [:p {:role "alert"}
        (tr [:stamp/invalid-definition])]

       draft
       [builder/editor
        (assoc data
               :tr tr
               :on-change #(rf/dispatch-sync [::subs/change-draft %])
               :on-upload #(>evt [::subs/upload %])
               :on-save #(>evt [::subs/save return-route])
               :on-cancel #(>evt [::subs/request-leave return-route (tr [:stamp/discard])]))]

       error
       [:div
        [:p {:role "alert"}
         (tr [error])]
        [ui/button
         {:kind     :tertiary
          :on-click #(>evt [::subs/load-editor group-id stamp-id])}
         (tr [:stamp/retry])]])]))

(defn editor-screen [tr group-id]
  (let [back-href     (table-href group-id)
        return-route  (table-location group-id)
        back!         #(>evt [::subs/request-leave return-route (tr [:stamp/discard])])
        back-click
        (fn [event]
          ;; Intercept ordinary navigation only; modifier-clicks must retain browser tab/window behavior.
          (when (and back-href (= 0 (.-button event))
                     (not (or (.-ctrlKey event) (.-metaKey event) (.-shiftKey event) (.-altKey event)))
                     (= back-href (some-> (.-target event) (.closest "a[href]") (.getAttribute "href"))))
            (.preventDefault event)
            (.stopPropagation event)
            (back!)))
        before-unload #(rf/dispatch-sync [::subs/before-unload %])]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (.addEventListener js/document "click" back-click true)
         (.addEventListener js/window "beforeunload" before-unload))
       :component-will-unmount
       (fn [_]
         (.removeEventListener js/document "click" back-click true)
         (.removeEventListener js/window "beforeunload" before-unload))
       :reagent-render
       (fn [tr _group-id]
         [builder-view tr])})))

(defn view [match]
  (let [{:keys [group-id stamp-id]} (-> match :parameters :path)
        tr                          (<sub [::gas/group-tr])
        group                       (<sub [::gas/group])
        user                        (<sub [:session/user])]
    [acommon/admin-page-v2
     {:match               match
      :title               :stamp/stamps
      :wait-for-group?     true
      :allowed-group-types group-model/authority-group-types}
     (when (and (= group-id (:db/id group))
                (<sub [::ff/enabled? :feature-flag.id/pdf-markup-tool])
                (permission-resolver/permitted? group user :permission.group/admin))
       [acommon/section-title-wrapper {:hide-title? true}
        ^{:key [group-id stamp-id]}
        [editor-screen tr group-id]])]))

(def editor-controller
  {:parameters {:path [:group-id :stamp-id]}
   :start      (fn [{{:keys [group-id stamp-id]} :path}]
                 (>evt [::subs/load-editor group-id stamp-id]))
   :stop       (fn [_] (>evt [::subs/clear-editor]))})

(def new-route
  {:name        :view/group.admin.stamp.new
   :own-layout? true
   :view        view
   :view-sub    [::view-data]
   :breadcrumb  {:items [stamps-breadcrumb builder-breadcrumb]}
   :parameters  {:path {:group-id s/Int}}
   :controllers [editor-controller]})

(def edit-route
  {:name        :view/group.admin.stamp.edit
   :own-layout? true
   :view        view
   :view-sub    [::view-data]
   :breadcrumb  {:items [stamps-breadcrumb builder-breadcrumb]}
   :parameters  {:path {:group-id s/Int :stamp-id s/Int}}
   :controllers [editor-controller]})
